import { createSelector } from '@reduxjs/toolkit';

import { selectContentEntries } from 'src/redux/content/selectors/selectContentEntries';
import { toMatchKey } from 'src/redux/content/utils/toMatchKey';
import { selectRouteParseOptions } from 'src/redux/productsQuery/selectors/selectRouteParseOptions';

/**
 * The set of match keys ({@link toMatchKey}) for the neighbourhood map's
 * indexable entries — the canonical lookup index. Derived once per map/route
 * change (memoised), so a followability check is a single key derivation plus an
 * O(1) set lookup, not a re-scan of every entry.
 */
const selectIndexableMatchKeys = createSelector(
  [selectContentEntries, selectRouteParseOptions],
  (entries, options): Set<string> => {
    const keys = new Set<string>();
    if (!options) {
      return keys;
    }
    entries.forEach((entry) => {
      if (!entry.indexable) {
        return;
      }
      const key = toMatchKey(entry.url, options);
      if (key !== null) {
        keys.add(key);
      }
    });

    return keys;
  },
);

/**
 * The single followability/indexability predicate: a URL is followable iff it is
 * exactly an indexable entry in the current neighbourhood map. Applied to a
 * link's href (crawlable link vs client-side action) and to the current URL
 * (page meta robots — see {@link selectIsCurrentUrlIndexable}), so link-level and
 * page-level directives can never drift.
 */
export const selectIsHrefFollowable = createSelector(
  [selectIndexableMatchKeys, selectRouteParseOptions],
  (indexableKeys, options) =>
    (href: string): boolean => {
      if (!options) {
        return false;
      }
      const key = toMatchKey(href, options);

      return key !== null && indexableKeys.has(key);
    },
);
